import { UI } from "../art/pal.ts";
import { drawText, FONT_H, measureText, wrapPx } from "./font.ts";

/** Native 16:9 frame. Every scene draws into this buffer; the presenter scales it to the window. */
export const SCREEN_W = 384;
export const SCREEN_H = 216;
export const VIEW_W = SCREEN_W;
export const VIEW_H = SCREEN_H;
/** Kept for older call sites that sized the overworld separately. */
export const OW_W = SCREEN_W;
export const OW_H = SCREEN_H;

export type Rgba = readonly [number, number, number, number];
export type Rgb = readonly [number, number, number];

export interface BlitOpts {
  flipX?: boolean;
  alpha?: number;
  scale?: number;
  /** Solid colour overlay 0..1 (white flash, red capture tint, silhouettes). */
  tint?: Rgba;
  tintAmt?: number;
}

export class Gfx {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  img: ImageData;
  /** Display scale (CSS px per native px). */
  scale = 1;
  reduced = false;
  /** Drawing origin offset (applied to every primitive). */
  ox = 0;
  oy = 0;
  private cx0 = 0;
  private cy0 = 0;
  private cx1 = SCREEN_W;
  private cy1 = SCREEN_H;
  /** Screen shake in native pixels, applied by the presenter at display resolution. */
  shakeX = 0;
  shakeY = 0;
  private native: HTMLCanvasElement | null = null;
  private nctx: CanvasRenderingContext2D | null = null;
  private k = 1;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) throw new Error("canvas");
    this.ctx = ctx;
    this.img = new ImageData(SCREEN_W, SCREEN_H);
    // The frame is opaque: alpha is set once here and never written transparent afterwards.
    for (let i = 3; i < this.img.data.length; i += 4) this.img.data[i] = 255;
    this.fit();
    window.addEventListener?.("resize", () => this.fit());
  }

  get vw(): number {
    return SCREEN_W;
  }

  get vh(): number {
    return SCREEN_H;
  }

  /** Reset origin and clip for a new drawing pass. */
  beginFrame(): void {
    this.ox = this.oy = 0;
    this.cx0 = this.cy0 = 0;
    this.cx1 = SCREEN_W;
    this.cy1 = SCREEN_H;
  }

  /** Restrict drawing to a rectangle (in current window coordinates). */
  setClip(x: number, y: number, w: number, h: number): void {
    this.cx0 = Math.max(0, x + this.ox);
    this.cy0 = Math.max(0, y + this.oy);
    this.cx1 = Math.min(SCREEN_W, x + w + this.ox);
    this.cy1 = Math.min(SCREEN_H, y + h + this.oy);
  }

  resetClip(): void {
    this.cx0 = this.cy0 = 0;
    this.cx1 = SCREEN_W;
    this.cy1 = SCREEN_H;
  }

  fit(): void {
    let w = window.innerWidth || SCREEN_W;
    let h = window.innerHeight || SCREEN_H;
    // A phone held upright turns the shell a quarter turn (style.css): fit the shell's own landscape box.
    const shell = typeof matchMedia === "function" && matchMedia("(pointer: coarse) and (orientation: portrait) and (max-width: 600px)").matches
      ? document.getElementById("shell")
      : null;
    if (shell && shell.clientWidth > shell.clientHeight) {
      w = shell.clientWidth;
      h = shell.clientHeight;
    }
    const touch = w < 820 || (typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches);
    const portrait = h > w;
    // Touch in landscape: a side column on each edge for the D-pad and the A/B buttons (see style.css),
    // so the controls never sit on top of the game.
    const gutter = touch && !portrait ? Math.min(170, Math.max(120, w * 0.16)) : 0;
    const aw = Math.max(SCREEN_W / 2, w - (touch ? 2 * gutter : 32));
    const ah = Math.max(SCREEN_H / 2, h - (touch && portrait ? Math.min(300, h * 0.42) : touch ? 0 : 32));
    let fit = Math.min(aw / SCREEN_W, ah / SCREEN_H);
    // Snap to an integer scale when we are within a few percent: perfectly square pixels.
    if (fit >= 1 && fit - Math.floor(fit) < 0.08) fit = Math.floor(fit);
    this.scale = fit;
    this.k = Math.max(1, Math.min(8, Math.ceil(fit - 1e-6)));
    if (this.canvas.width !== SCREEN_W * this.k) {
      this.canvas.width = SCREEN_W * this.k;
      this.canvas.height = SCREEN_H * this.k;
    }
    if (this.canvas.style) {
      this.canvas.style.width = `${Math.round(SCREEN_W * fit)}px`;
      this.canvas.style.height = `${Math.round(SCREEN_H * fit)}px`;
    }
  }

  clear(c: Rgba = UI.bg0): void {
    const d = this.img.data;
    const r = c[0], g = c[1], b = c[2];
    for (let i = 0; i < d.length; i += 4) {
      d[i] = r;
      d[i + 1] = g;
      d[i + 2] = b;
      d[i + 3] = 255;
    }
  }

  p(x: number, y: number, c: Rgba): void {
    const xi = (x | 0) + this.ox;
    const yi = (y | 0) + this.oy;
    if (xi < this.cx0 || yi < this.cy0 || xi >= this.cx1 || yi >= this.cy1) return;
    const o = (yi * SCREEN_W + xi) * 4;
    const d = this.img.data;
    const a = c[3];
    if (a >= 255) {
      d[o] = c[0];
      d[o + 1] = c[1];
      d[o + 2] = c[2];
      return;
    }
    if (a <= 0) return;
    const t = a / 255;
    d[o] = d[o]! + (c[0] - d[o]!) * t;
    d[o + 1] = d[o + 1]! + (c[1] - d[o + 1]!) * t;
    d[o + 2] = d[o + 2]! + (c[2] - d[o + 2]!) * t;
  }

  /** Additive light: adds `c * k` to the pixel. */
  add(x: number, y: number, c: Rgba, k = 1): void {
    const xi = (x | 0) + this.ox;
    const yi = (y | 0) + this.oy;
    if (xi < this.cx0 || yi < this.cy0 || xi >= this.cx1 || yi >= this.cy1) return;
    const o = (yi * SCREEN_W + xi) * 4;
    const d = this.img.data;
    const t = (c[3] / 255) * k;
    d[o] = d[o]! + c[0] * t;
    d[o + 1] = d[o + 1]! + c[1] * t;
    d[o + 2] = d[o + 2]! + c[2] * t;
  }

  fill(x: number, y: number, w: number, h: number, c: Rgba): void {
    const x0 = Math.max(this.cx0, (x | 0) + this.ox);
    const y0 = Math.max(this.cy0, (y | 0) + this.oy);
    const x1 = Math.min(this.cx1, (x | 0) + (w | 0) + this.ox);
    const y1 = Math.min(this.cy1, (y | 0) + (h | 0) + this.oy);
    if (x1 <= x0 || y1 <= y0) return;
    const d = this.img.data;
    const a = c[3];
    if (a <= 0) return;
    if (a >= 255) {
      for (let yy = y0; yy < y1; yy++) {
        let o = (yy * SCREEN_W + x0) * 4;
        for (let xx = x0; xx < x1; xx++, o += 4) {
          d[o] = c[0];
          d[o + 1] = c[1];
          d[o + 2] = c[2];
        }
      }
      return;
    }
    const t = a / 255;
    const r = c[0] * t, gg = c[1] * t, b = c[2] * t, it = 1 - t;
    for (let yy = y0; yy < y1; yy++) {
      let o = (yy * SCREEN_W + x0) * 4;
      for (let xx = x0; xx < x1; xx++, o += 4) {
        d[o] = d[o]! * it + r;
        d[o + 1] = d[o + 1]! * it + gg;
        d[o + 2] = d[o + 2]! * it + b;
      }
    }
  }

  rect(x: number, y: number, w: number, h: number, c: Rgba): void {
    this.fill(x, y, w, 1, c);
    this.fill(x, y + h - 1, w, 1, c);
    this.fill(x, y + 1, 1, h - 2, c);
    this.fill(x + w - 1, y + 1, 1, h - 2, c);
  }

  hline(x: number, y: number, w: number, c: Rgba): void {
    this.fill(x, y, w, 1, c);
  }

  vline(x: number, y: number, h: number, c: Rgba): void {
    this.fill(x, y, 1, h, c);
  }

  /** Row inset for a rounded corner of radius r at row i (0 = outer row). */
  static inset(r: number, i: number): number {
    if (i >= r) return 0;
    const dy = r - i - 0.5;
    return Math.round(r - Math.sqrt(Math.max(0, r * r - dy * dy)));
  }

  /** Filled rounded rectangle. */
  rrect(x: number, y: number, w: number, h: number, r: number, c: Rgba): void {
    r = Math.min(r, w >> 1, h >> 1);
    for (let i = 0; i < h; i++) {
      const k = i < r ? Gfx.inset(r, i) : i >= h - r ? Gfx.inset(r, h - 1 - i) : 0;
      this.fill(x + k, y + i, w - 2 * k, 1, c);
    }
  }

  /** Rounded rectangle outline (1px). */
  rrectLine(x: number, y: number, w: number, h: number, r: number, c: Rgba): void {
    r = Math.min(r, w >> 1, h >> 1);
    for (let i = 0; i < h; i++) {
      const k = i < r ? Gfx.inset(r, i) : i >= h - r ? Gfx.inset(r, h - 1 - i) : 0;
      const prev = i === 0 ? w : i - 1 < r ? Gfx.inset(r, i - 1) : i - 1 >= h - r ? Gfx.inset(r, h - i) : 0;
      const next = i === h - 1 ? w : i + 1 < r ? Gfx.inset(r, i + 1) : i + 1 >= h - r ? Gfx.inset(r, h - 2 - i) : 0;
      if (i === 0 || i === h - 1) {
        this.fill(x + k, y + i, w - 2 * k, 1, c);
        continue;
      }
      const span = Math.max(1, Math.max(prev, next) - k);
      this.fill(x + k, y + i, Math.min(span, w), 1, c);
      this.fill(x + w - k - Math.min(span, w), y + i, Math.min(span, w), 1, c);
    }
  }

  /** Vertical gradient fill (optionally rounded). */
  gradV(x: number, y: number, w: number, h: number, c0: Rgba, c1: Rgba, r = 0): void {
    r = Math.min(r, w >> 1, h >> 1);
    for (let i = 0; i < h; i++) {
      const t = h <= 1 ? 0 : i / (h - 1);
      const c: Rgba = [
        c0[0] + (c1[0] - c0[0]) * t,
        c0[1] + (c1[1] - c0[1]) * t,
        c0[2] + (c1[2] - c0[2]) * t,
        c0[3] + (c1[3] - c0[3]) * t,
      ];
      const k = r ? (i < r ? Gfx.inset(r, i) : i >= h - r ? Gfx.inset(r, h - 1 - i) : 0) : 0;
      this.fill(x + k, y + i, w - 2 * k, 1, c);
    }
  }

  /** Horizontal gradient fill. */
  gradH(x: number, y: number, w: number, h: number, c0: Rgba, c1: Rgba): void {
    for (let i = 0; i < w; i++) {
      const t = w <= 1 ? 0 : i / (w - 1);
      this.fill(x + i, y, 1, h, [
        c0[0] + (c1[0] - c0[0]) * t,
        c0[1] + (c1[1] - c0[1]) * t,
        c0[2] + (c1[2] - c0[2]) * t,
        c0[3] + (c1[3] - c0[3]) * t,
      ]);
    }
  }

  /** Filled ellipse; `soft` feathers the rim (0 = hard pixel edge). */
  ellipse(cx: number, cy: number, rx: number, ry: number, c: Rgba, soft = 0): void {
    if (rx <= 0 || ry <= 0) return;
    const y0 = Math.floor(cy - ry), y1 = Math.ceil(cy + ry);
    const x0 = Math.floor(cx - rx), x1 = Math.ceil(cx + rx);
    for (let y = y0; y <= y1; y++) {
      const ny = (y + 0.5 - cy) / ry;
      for (let x = x0; x <= x1; x++) {
        const nx = (x + 0.5 - cx) / rx;
        const d2 = nx * nx + ny * ny;
        if (d2 > 1) continue;
        if (soft > 0) {
          const edge = Math.min(1, (1 - Math.sqrt(d2)) / soft);
          this.p(x, y, [c[0], c[1], c[2], c[3] * edge * edge * (3 - 2 * edge)]);
        } else this.p(x, y, c);
      }
    }
  }

  /** Elliptical ring of the given thickness (in pixels). */
  ring(cx: number, cy: number, rx: number, ry: number, c: Rgba, thick = 1.5): void {
    if (rx <= 0 || ry <= 0) return;
    const y0 = Math.floor(cy - ry - 1), y1 = Math.ceil(cy + ry + 1);
    const x0 = Math.floor(cx - rx - 1), x1 = Math.ceil(cx + rx + 1);
    const rr = (rx + ry) / 2;
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const nx = (x + 0.5 - cx) / rx, ny = (y + 0.5 - cy) / ry;
        const d = Math.abs(Math.sqrt(nx * nx + ny * ny) - 1) * rr;
        if (d <= thick / 2) this.p(x, y, c);
      }
    }
  }

  disc(cx: number, cy: number, r: number, c: Rgba, soft = 0): void {
    this.ellipse(cx, cy, r, r, c, soft);
  }

  /** Additive radial glow. */
  glow(cx: number, cy: number, r: number, c: Rgba, k = 1): void {
    if (r <= 0) return;
    const y0 = Math.floor(cy - r), y1 = Math.ceil(cy + r);
    const x0 = Math.floor(cx - r), x1 = Math.ceil(cx + r);
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const dx = (x + 0.5 - cx) / r, dy = (y + 0.5 - cy) / r;
        const d2 = dx * dx + dy * dy;
        if (d2 >= 1) continue;
        const f = (1 - d2) * (1 - d2);
        this.add(x, y, c, f * k);
      }
    }
  }

  line(x0: number, y0: number, x1: number, y1: number, c: Rgba, width = 1): void {
    const n = Math.max(1, Math.ceil(Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0))));
    const off = (width - 1) >> 1;
    for (let i = 0; i <= n; i++) {
      const x = Math.round(x0 + ((x1 - x0) * i) / n) - off;
      const y = Math.round(y0 + ((y1 - y0) * i) / n) - off;
      if (width <= 1) this.p(x, y, c);
      else this.fill(x, y, width, width, c);
    }
  }

  /** Darken (multiply) a rectangle toward black by `k` (0..1). */
  dim(x: number, y: number, w: number, h: number, k: number): void {
    this.fill(x, y, w, h, [0, 0, 0, Math.round(255 * k)]);
  }

  text(x: number, y: number, s: string, c: Rgba = UI.text, wrap = 0): number {
    return drawText(this.img, (x | 0) + this.ox, (y | 0) + this.oy, s, c, wrap, 1, this.clipRect()) - this.oy;
  }

  /** Decorative copy of a string (shadows, outlines): drawn, but not a separate readable label. */
  private ghost(x: number, y: number, s: string, c: Rgba, scale = 1): void {
    drawText(this.img, (x | 0) + this.ox, (y | 0) + this.oy, s, c, 0, scale, this.clipRect());
  }

  /** Scaled text (integer scale). */
  textBig(x: number, y: number, s: string, c: Rgba, scale = 2): number {
    return drawText(this.img, (x | 0) + this.ox, (y | 0) + this.oy, s, c, 0, scale, this.clipRect()) - this.oy;
  }

  textW(s: string, scale = 1): number {
    return measureText(s, scale);
  }

  textShadow(x: number, y: number, s: string, c: Rgba = UI.text, sh: Rgba = UI.ink, scale = 1): void {
    this.ghost(x, y + scale, s, sh, scale);
    if (scale === 1) this.text(x, y, s, c);
    else this.textBig(x, y, s, c, scale);
  }

  /** Text with a 1px outline on all 8 sides. */
  textOutline(x: number, y: number, s: string, c: Rgba, ol: Rgba = UI.ink, scale = 1): void {
    for (let oy = -1; oy <= 1; oy++) {
      for (let ox = -1; ox <= 1; ox++) if (ox || oy) this.ghost(x + ox, y + oy, s, ol, scale);
    }
    this.ghost(x, y + 1 + scale, s, ol, scale);
    if (scale === 1) this.text(x, y, s, c);
    else this.textBig(x, y, s, c, scale);
  }

  /** Centre `s` on `cx`. */
  textC(cx: number, y: number, s: string, c: Rgba = UI.text, scale = 1): void {
    const w = measureText(s, scale);
    if (scale === 1) this.text(Math.round(cx - w / 2), y, s, c);
    else this.textBig(Math.round(cx - w / 2), y, s, c, scale);
  }

  /** Right-align `s` to end at `rx`. */
  textR(rx: number, y: number, s: string, c: Rgba = UI.text): void {
    this.text(rx - measureText(s), y, s, c);
  }

  /** Wrapped paragraph at a pixel width. Returns the y below the block. */
  para(x: number, y: number, s: string, w: number, c: Rgba = UI.text, lineH = FONT_H, maxLines = 99): number {
    const rows = wrapPx(s, w).slice(0, maxLines);
    let yy = y;
    for (const row of rows) {
      this.text(x, yy, row, c);
      yy += lineH;
    }
    return yy;
  }

  private clipRect() {
    return { x0: this.cx0, y0: this.cy0, x1: this.cx1, y1: this.cy1 };
  }

  blit(src: ImageData, dx: number, dy: number, opts?: BlitOpts): void {
    const scale = Math.max(1, (opts?.scale ?? 1) | 0);
    if (scale > 1) {
      this.blitScaled(src, dx, dy, src.width * scale, src.height * scale, opts);
      return;
    }
    const alpha = opts?.alpha ?? 1;
    if (alpha <= 0) return;
    const flip = opts?.flipX ?? false;
    const tint = opts?.tint;
    const ta = tint ? Math.max(0, Math.min(1, opts?.tintAmt ?? 1)) : 0;
    const sw = src.width, sh = src.height, s = src.data, d = this.img.data;
    const X0 = (dx | 0) + this.ox, Y0 = (dy | 0) + this.oy;
    const xa = Math.max(0, this.cx0 - X0), xb = Math.min(sw, this.cx1 - X0);
    const ya = Math.max(0, this.cy0 - Y0), yb = Math.min(sh, this.cy1 - Y0);
    for (let y = ya; y < yb; y++) {
      let o = ((Y0 + y) * SCREEN_W + X0 + xa) * 4;
      for (let x = xa; x < xb; x++, o += 4) {
        const si = (y * sw + (flip ? sw - 1 - x : x)) * 4;
        const sa = s[si + 3]!;
        if (sa === 0) continue;
        let r = s[si]!, g = s[si + 1]!, b = s[si + 2]!;
        if (tint) {
          r += (tint[0] - r) * ta;
          g += (tint[1] - g) * ta;
          b += (tint[2] - b) * ta;
        }
        const a = (sa / 255) * alpha;
        if (a >= 1) {
          d[o] = r;
          d[o + 1] = g;
          d[o + 2] = b;
        } else {
          d[o] = d[o]! + (r - d[o]!) * a;
          d[o + 1] = d[o + 1]! + (g - d[o + 1]!) * a;
          d[o + 2] = d[o + 2]! + (b - d[o + 2]!) * a;
        }
      }
    }
  }

  /** Nearest-neighbour blit into an arbitrary destination size (squash / stretch / zoom). */
  blitScaled(src: ImageData, dx: number, dy: number, dw: number, dh: number, opts?: BlitOpts): void {
    const alpha = opts?.alpha ?? 1;
    if (alpha <= 0 || dw <= 0 || dh <= 0) return;
    const flip = opts?.flipX ?? false;
    const tint = opts?.tint;
    const ta = tint ? Math.max(0, Math.min(1, opts?.tintAmt ?? 1)) : 0;
    const sw = src.width, sh = src.height, s = src.data, d = this.img.data;
    const X0 = Math.round(dx) + this.ox, Y0 = Math.round(dy) + this.oy;
    const W = Math.round(dw), H = Math.round(dh);
    const xa = Math.max(0, this.cx0 - X0), xb = Math.min(W, this.cx1 - X0);
    const ya = Math.max(0, this.cy0 - Y0), yb = Math.min(H, this.cy1 - Y0);
    for (let y = ya; y < yb; y++) {
      const sy = Math.min(sh - 1, Math.floor((y * sh) / H));
      let o = ((Y0 + y) * SCREEN_W + X0 + xa) * 4;
      for (let x = xa; x < xb; x++, o += 4) {
        let sx = Math.min(sw - 1, Math.floor((x * sw) / W));
        if (flip) sx = sw - 1 - sx;
        const si = (sy * sw + sx) * 4;
        const sa = s[si + 3]!;
        if (sa === 0) continue;
        let r = s[si]!, g = s[si + 1]!, b = s[si + 2]!;
        if (tint) {
          r += (tint[0] - r) * ta;
          g += (tint[1] - g) * ta;
          b += (tint[2] - b) * ta;
        }
        const a = (sa / 255) * alpha;
        if (a >= 1) {
          d[o] = r;
          d[o + 1] = g;
          d[o + 2] = b;
        } else {
          d[o] = d[o]! + (r - d[o]!) * a;
          d[o + 1] = d[o + 1]! + (g - d[o + 1]!) * a;
          d[o + 2] = d[o + 2]! + (b - d[o + 2]!) * a;
        }
      }
    }
  }

  /** Opaque copy of a region of a large source (the pre-baked world ground). */
  copyRegion(src: ImageData, sx: number, sy: number, w: number, h: number, dx: number, dy: number): void {
    const s = src.data, d = this.img.data;
    for (let y = 0; y < h; y++) {
      const ty = dy + y + this.oy;
      const syy = sy + y;
      if (ty < this.cy0 || ty >= this.cy1 || syy < 0 || syy >= src.height) continue;
      let x0 = 0, x1 = w;
      if (dx + this.ox + x0 < this.cx0) x0 = this.cx0 - dx - this.ox;
      if (dx + this.ox + x1 > this.cx1) x1 = this.cx1 - dx - this.ox;
      if (sx + x0 < 0) x0 = -sx;
      if (sx + x1 > src.width) x1 = src.width - sx;
      if (x1 <= x0) continue;
      const so = (syy * src.width + sx + x0) * 4;
      const len = (x1 - x0) * 4;
      d.set(s.subarray(so, so + len), (ty * SCREEN_W + dx + this.ox + x0) * 4);
    }
  }

  present(): void {
    if (!this.native) {
      this.native = document.createElement("canvas");
      this.native.width = SCREEN_W;
      this.native.height = SCREEN_H;
      this.nctx = this.native.getContext("2d");
    }
    this.fit();
    this.nctx!.putImageData(this.img, 0, 0);
    const k = this.k;
    this.ctx.imageSmoothingEnabled = false;
    if (this.shakeX || this.shakeY) {
      this.ctx.fillStyle = "#07080f";
      this.ctx.fillRect(0, 0, SCREEN_W * k, SCREEN_H * k);
    }
    this.ctx.drawImage(this.native, Math.round(this.shakeX * k), Math.round(this.shakeY * k), SCREEN_W * k, SCREEN_H * k);
  }
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function clamp01(t: number): number {
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

export function easeOut(t: number): number {
  const u = clamp01(t);
  return 1 - (1 - u) ** 3;
}

export function easeInOut(t: number): number {
  const u = clamp01(t);
  return u < 0.5 ? 4 * u * u * u : 1 - (-2 * u + 2) ** 3 / 2;
}

/** Overshooting ease (UI pops). */
export function easeBack(t: number, s = 1.70158): number {
  const u = clamp01(t) - 1;
  return u * u * ((s + 1) * u + s) + 1;
}

export function approach(cur: number, target: number, step: number): number {
  if (cur < target) return Math.min(target, cur + step);
  if (cur > target) return Math.max(target, cur - step);
  return target;
}
